import * as THREE from 'three';
import Decoder from '../WasmModules/DracoDecoder';
import Encoder from '../WasmModules/DracoEncoder';
import FileSaver from 'file-saver';

class _GeometryBuffer {
  public indices?: Uint16Array | Uint32Array;

  public vertices?: Float32Array;

  public normals?: Float32Array;

  public uvs?: Float32Array;

  public colors?: Float32Array;
}

export async function DecodeDracoUrlToBufferGeometry(url: string): Promise<THREE.BufferGeometry> {
  const value = await fetch(url);
  const data = await value.blob();
  const buf = await data.arrayBuffer();
  return DecodeDracoToBufferGeometry(new Uint8Array(buf));
}

export function DecodeDracoToBufferGeometry(binaryData: Uint8Array): THREE.BufferGeometry {
  const isEmptyGeometry = binaryData.length == 7 && new TextDecoder().decode(binaryData) == '<empty>';
  if (isEmptyGeometry) {
    return new THREE.BufferGeometry();
  }

  //  const isTooLarge =
  //    !isEmptyGeometry &&
  //    $.browser.mobile &&
  //    binaryData.byteLength > 4 * 1024 * 1024; // 4Mb - impericaly found
  //
  //  if (isTooLarge) {
  //    console.warn(
  //      'Mesh is too large for mobile device. Decoding skipped, returning empty mesh...'
  //    );
  //  }
  //
  //  if (isEmptyGeometry || isTooLarge) {
  //    let geometry = new THREE.BufferGeometry();
  //    geometry.isEmpty = true;
  //    geometry.isTooLarge = isTooLarge;
  //    const vertices = new Float32Array([-1.0, -1.0, 1.0]);
  //    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  //    return geometry;
  //  }

  const decoder = new Decoder.Decoder();

  const geometryBuffer = new _GeometryBuffer();

  if (decoder.GetEncodedGeometryType(binaryData) === Decoder.TRIANGULAR_MESH) {
    const dracoGeometry = new Decoder.Mesh();
    const decodingStatus = decoder.DecodeArrayToMesh(binaryData, binaryData.byteLength, dracoGeometry);
    if (!decodingStatus.ok()) {
      throw new Error('Draco error: ' + decodingStatus.error_msg());
    }

    const numPoints = dracoGeometry.num_points();
    const numFaces = dracoGeometry.num_faces();
    const numVertexCoordinates = numPoints * 3;
    const numTextureCoordinates = numPoints * 2;
    const numIndices = numFaces * 3;

    // vertices
    if (numPoints > 0) {
      geometryBuffer.vertices = new Float32Array(numVertexCoordinates);
      const posAttId = decoder.GetAttributeId(dracoGeometry, Decoder.POSITION);
      const posAttribute = decoder.GetAttribute(dracoGeometry, posAttId);
      const posAttributeData = new Decoder.DracoFloat32Array();
      decoder.GetAttributeFloatForAllPoints(dracoGeometry, posAttribute, posAttributeData);
      for (let i = 0; i < numVertexCoordinates; i += 3) {
        geometryBuffer.vertices[i] = posAttributeData.GetValue(i);
        geometryBuffer.vertices[i + 1] = posAttributeData.GetValue(i + 1);
        geometryBuffer.vertices[i + 2] = posAttributeData.GetValue(i + 2);
      }
      Decoder._module.destroy(posAttributeData);
    }

    // indices
    if (numFaces > 0) {
      geometryBuffer.indices = new (numPoints > 65535 ? Uint32Array : Uint16Array)(numIndices);
      const ia = new Decoder.DracoInt32Array();
      for (let i = 0, j = 0; i < numFaces; ++i, j += 3) {
        decoder.GetFaceFromMesh(dracoGeometry, i, ia);
        geometryBuffer.indices[j] = ia.GetValue(0);
        geometryBuffer.indices[j + 1] = ia.GetValue(1);
        geometryBuffer.indices[j + 2] = ia.GetValue(2);
      }
      Decoder._module.destroy(ia);
    }

    // colors
    const colorAttId = decoder.GetAttributeId(dracoGeometry, Decoder.COLOR);
    if (colorAttId !== -1) {
      geometryBuffer.colors = new Float32Array(numVertexCoordinates);
      const colAttribute = decoder.GetAttribute(dracoGeometry, colorAttId);
      const colAttributeData = new Decoder.DracoFloat32Array();
      decoder.GetAttributeFloatForAllPoints(dracoGeometry, colAttribute, colAttributeData);
      for (let i = 0; i < numVertexCoordinates; i += 3) {
        geometryBuffer.colors[i] = colAttributeData.GetValue(i);
        geometryBuffer.colors[i + 1] = colAttributeData.GetValue(i + 1);
        geometryBuffer.colors[i + 2] = colAttributeData.GetValue(i + 2);
      }
      Decoder._module.destroy(colAttributeData);
    }

    // normals
    const normalAttId = decoder.GetAttributeId(dracoGeometry, Decoder.NORMAL);
    if (normalAttId !== -1) {
      geometryBuffer.normals = new Float32Array(numVertexCoordinates);
      const norAttribute = decoder.GetAttribute(dracoGeometry, normalAttId);
      const norAttributeData = new Decoder.DracoFloat32Array();
      decoder.GetAttributeFloatForAllPoints(dracoGeometry, norAttribute, norAttributeData);
      for (let i = 0; i < numVertexCoordinates; i += 3) {
        geometryBuffer.normals[i] = norAttributeData.GetValue(i);
        geometryBuffer.normals[i + 1] = norAttributeData.GetValue(i + 1);
        geometryBuffer.normals[i + 2] = norAttributeData.GetValue(i + 2);
      }
      Decoder._module.destroy(norAttributeData);
    }

    // uvs
    const texCoordAttId = decoder.GetAttributeId(dracoGeometry, Decoder.TEX_COORD);
    if (texCoordAttId !== -1) {
      geometryBuffer.uvs = new Float32Array(numTextureCoordinates);
      const texCoordAttribute = decoder.GetAttribute(dracoGeometry, texCoordAttId);
      const textCoordAttributeData = new Decoder.DracoFloat32Array();
      decoder.GetAttributeFloatForAllPoints(dracoGeometry, texCoordAttribute, textCoordAttributeData);
      for (let i = 0; i < numTextureCoordinates; i += 2) {
        geometryBuffer.uvs[i] = textCoordAttributeData.GetValue(i);
        geometryBuffer.uvs[i + 1] = textCoordAttributeData.GetValue(i + 1);
      }
      Decoder._module.destroy(textCoordAttributeData);
    }

    Decoder._module.destroy(dracoGeometry);
  } else {
    const dracoGeometry = new Decoder.PointCloud();
    const decodingStatus = decoder.DecodeArrayToPointCloud(binaryData, binaryData.byteLength, dracoGeometry);
    if (!decodingStatus.ok()) {
      throw new Error('Draco error: ' + decodingStatus.error_msg());
    }

    const numPoints = dracoGeometry.num_points();
    const numVertexCoordinates = numPoints * 3;

    // vertices
    if (numPoints > 0) {
      geometryBuffer.vertices = new Float32Array(numVertexCoordinates);
      const posAttId = decoder.GetAttributeId(dracoGeometry, Decoder.POSITION);
      const posAttribute = decoder.GetAttribute(dracoGeometry, posAttId);
      const posAttributeData = new Decoder.DracoFloat32Array();
      decoder.GetAttributeFloatForAllPoints(dracoGeometry, posAttribute, posAttributeData);
      for (let i = 0; i < numVertexCoordinates; i += 3) {
        geometryBuffer.vertices[i] = posAttributeData.GetValue(i);
        geometryBuffer.vertices[i + 1] = posAttributeData.GetValue(i + 1);
        geometryBuffer.vertices[i + 2] = posAttributeData.GetValue(i + 2);
      }
      Decoder._module.destroy(posAttributeData);
    }

    Decoder._module.destroy(dracoGeometry);
  }

  Decoder._module.destroy(decoder);

  const indices = geometryBuffer.indices;
  const positions = geometryBuffer.vertices;
  const normals = geometryBuffer.normals;
  const uvs = geometryBuffer.uvs;
  const colors = geometryBuffer.colors;

  const geometry = new THREE.BufferGeometry();
  if (indices !== undefined && indices.length > 0) {
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  }
  if (!positions) {
    throw new Error('Logic error');
  }
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  if (normals && normals.length > 0) {
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  }
  if (uvs && uvs.length > 0) {
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  }
  if (colors && colors.length > 0) {
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  }

  if (geometry.index) {
    // Workaround for inability to render more than 2^20 triangles at once on some environments
    const maxIndicesCount = 1048575 * 3; // 1048575 = (2^20 - 1) = (GL_MAX_ELEMENTS_INDICES - 1)
    const indicesCount = geometry.index.array.length;
    if (indicesCount > maxIndicesCount) {
      for (let groupStartIdx = 0; groupStartIdx < indicesCount; groupStartIdx += maxIndicesCount) {
        if (groupStartIdx + maxIndicesCount > indicesCount) {
          geometry.addGroup(groupStartIdx, indicesCount - groupStartIdx);
        } else {
          geometry.addGroup(groupStartIdx, maxIndicesCount);
        }
      }
    } else if (geometry.groups.length === 0) {
      //workaround: in new three.js it seems that groups are not obligatory
      geometry.addGroup(0, indicesCount);
    }

    // compute vertex normals if not present in the CTM model
    if (geometry.attributes.normal === undefined) {
      geometry.computeVertexNormals();
    }
  }

  return geometry;
}

export function EncodeMeshToDraco(
  vertices: Float32Array,
  indices: Int32Array,
  normals?: Float32Array,
  colors?: Float32Array,
  texCoords?: Float32Array
): Int8Array<ArrayBuffer> {
  const shouldPreserveVerticesOrder = false;
  const positionQuantization = 12;
  const normalQuantization = 12;
  const colorQuantization = 10;
  const texCoordQuantization = 10;
  const compressionLevel = 5;

  const speed = 10 - compressionLevel;
  const method = shouldPreserveVerticesOrder ? Encoder.MESH_SEQUENTIAL_ENCODING : Encoder.MESH_EDGEBREAKER_ENCODING;

  const mesh = new Encoder.Mesh();

  const meshBuilder = new Encoder.MeshBuilder();

  const encoder = new Encoder.Encoder();
  encoder.SetEncodingMethod(method);
  encoder.SetSpeedOptions(speed, speed);

  const vertexCount = vertices.length / 3;
  const trianglesCount = indices.length / 3;

  meshBuilder.AddFloatAttribute(mesh, Encoder.POSITION, vertexCount, 3, vertices);
  encoder.SetAttributeQuantization(Encoder.POSITION, positionQuantization);

  meshBuilder.AddFacesToMesh(mesh, trianglesCount, indices);

  if (normals) {
    meshBuilder.AddFloatAttributeToMesh(mesh, Encoder.NORMAL, vertexCount, 3, normals);
    encoder.SetAttributeQuantization(Encoder.NORMAL, normalQuantization);
  }
  if (colors) {
    meshBuilder.AddFloatAttributeToMesh(mesh, Encoder.COLOR, vertexCount, 3, colors);
    encoder.SetAttributeQuantization(Encoder.COLOR, colorQuantization);
  }
  if (texCoords) {
    meshBuilder.AddFloatAttributeToMesh(mesh, Encoder.TEX_COORD, vertexCount, 3, texCoords);
    encoder.SetAttributeQuantization(Encoder.TEX_COORD, texCoordQuantization);
  }

  Encoder._module.destroy(meshBuilder);

  const buffer = new Encoder.DracoInt8Array();

  const encodedLen = encoder.EncodeMeshToDracoBuffer(mesh, buffer);

  Encoder._module.destroy(mesh);
  Encoder._module.destroy(encoder);

  if (encodedLen > buffer.size()) {
    throw new Error('DRACO decoding failed');
  }

  const ret = new Int8Array(encodedLen);
  for (let i = 0; i < encodedLen; ++i) {
    ret[i] = buffer.GetValue(i);
  }

  Encoder._module.destroy(buffer);

  return ret;
}

export function EncodePointCloudToDraco(vertices: Float32Array): Int8Array<ArrayBuffer> {
  const shouldPreserveVerticesOrder = true;
  const positionQuantization = 12;
  const compressionLevel = 5;

  const speed = 10 - compressionLevel;
  const method = shouldPreserveVerticesOrder ? Encoder.MESH_SEQUENTIAL_ENCODING : Encoder.MESH_EDGEBREAKER_ENCODING;

  const pc = new Encoder.PointCloud();

  const pcBuilder = new Encoder.PointCloudBuilder();

  const encoder = new Encoder.Encoder();
  encoder.SetEncodingMethod(method);
  encoder.SetSpeedOptions(speed, speed);

  const vertexCount = vertices.length / 3;

  pcBuilder.AddFloatAttribute(pc, Encoder.POSITION, vertexCount, 3, vertices);
  encoder.SetAttributeQuantization(Encoder.POSITION, positionQuantization);

  Encoder._module.destroy(pcBuilder);

  const buffer = new Encoder.DracoInt8Array();

  const encodedLen = encoder.EncodePointCloudToDracoBuffer(pc, false, buffer);

  Encoder._module.destroy(pc);
  Encoder._module.destroy(encoder);

  if (encodedLen > buffer.size()) {
    throw new Error('DRACO decoding failed');
  }

  const ret = new Int8Array(encodedLen);
  for (let i = 0; i < encodedLen; ++i) {
    ret[i] = buffer.GetValue(i);
  }

  Encoder._module.destroy(buffer);

  return ret;
}

export function EncodeMeshGeometryToDraco(bufferGeometry: THREE.BufferGeometry): Int8Array<ArrayBuffer> {
  if (!bufferGeometry.index) {
    throw new Error('Invalid mesh');
  }
  const vertices = Float32Array.from(bufferGeometry.attributes.position.array);
  const indices = Int32Array.from(bufferGeometry.index.array);
  return EncodeMeshToDraco(vertices, indices);
}

export function EncodePointCloudGeometryToDraco(bufferGeometry: THREE.BufferGeometry): Int8Array<ArrayBuffer> {
  const vertices = Float32Array.from(bufferGeometry.attributes.position.array);
  return EncodePointCloudToDraco(vertices);
}

export function EncodePointsArrayToDraco(points: Array<THREE.Vector3>): Int8Array<ArrayBuffer> {
  const vertices = new Float32Array(points.length * 3);
  for (let i = 0, sz = points.length; i < sz; ++i) {
    points[i].toArray(vertices, i * 3);
  }
  return EncodePointCloudToDraco(vertices);
}

export function DownloadGeometryAsDraco(bufferGeometry: THREE.BufferGeometry, name: string): void {
  const array = bufferGeometry.index?.array;
  const isMeshGeometry = array && array.length > 0;
  const drc = isMeshGeometry
    ? EncodeMeshGeometryToDraco(bufferGeometry)
    : EncodePointCloudGeometryToDraco(bufferGeometry);
  const blob = new Blob([drc], { type: 'octet/stream' });
  FileSaver.saveAs(blob, name);
}
